import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Link,
    Paper,
    Rating,
    MenuItem,
    Stack,
    TextField,
    Typography,
    alpha,
} from "@mui/material";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_URL, APP_URL, buildAppUrl } from "./config";

export default function SurveyPublicPage() {
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [survey, setSurvey] = useState(null);
    const [answers, setAnswers] = useState({});
    const [message, setMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [rewardResult, setRewardResult] = useState(null);
    const [recordMissing, setRecordMissing] = useState(false);

    useEffect(() => {
        const loadSurvey = async () => {
            setLoading(true);
            setMessage("");
            setErrorMessage("");
            setRewardResult(null);
            setRecordMissing(false);

            try {
                const response = await axios.get(`${API_URL}get_survey.php?id=${encodeURIComponent(id)}`, {
                    withCredentials: true,
                });
                const data = response.data || {};
                setSurvey(data);

                const initial = {};
                (data.questions || []).forEach((question, index) => {
                    if (question?.type === "star" || question?.type === "emoji") {
                        initial[`q_${index}`] = 0;
                        return;
                    }
                    if (question?.type === "thumbs") {
                        initial[`q_${index}`] = "";
                        return;
                    }
                    initial[`q_${index}`] = "";
                });
                setAnswers(initial);
            } catch (error) {
                console.error("Failed to load survey", error);
                const missing = error.response?.status === 404;
                setRecordMissing(missing);
                setErrorMessage(missing
                    ? "This record does not exist."
                    : (error.response?.data?.message || "Unable to load this survey right now."));
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            loadSurvey();
        }
    }, [id]);

    const canSubmit = useMemo(() => {
        if (!survey) return false;
        if (survey.require_login && !survey.is_logged_in) return false;
        return true;
    }, [survey]);

    const completionRewardSummary = useMemo(() => {
        if (!survey?.completion_rewards) {
            return null;
        }

        const giftLimit = Number(survey.completion_rewards.gift_limit || 0);
        const distributionMode = survey.completion_rewards.distribution_mode === "random" ? "random" : "sequential";
        const hasConfiguredReward = [
            survey.completion_rewards.gift_voucher,
            survey.completion_rewards.discount_code,
            survey.completion_rewards.prize_draw,
        ].some((item) => String(item || "").trim() !== "");

        if (!hasConfiguredReward) {
            return null;
        }

        return {
            giftLimit,
            distributionMode,
        };
    }, [survey]);

    const handleAnswerChange = (index, value) => {
        setAnswers((prev) => ({ ...prev, [`q_${index}`]: value }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!survey) return;

        const payloadAnswers = (survey.questions || []).map((question, index) => {
            const value = answers[`q_${index}`];
            if (question?.type === "star") {
                return {
                    question_index: index,
                    type: question?.type || "text",
                    question: question?.label || "",
                    answer: Number(value || 0),
                };
            }

            return {
                question_index: index,
                type: question?.type || "text",
                question: question?.label || "",
                answer: String(value || "").trim(),
            };
        }).filter((item) => item.answer !== "" && item.answer !== 0);

        if (payloadAnswers.length === 0) {
            setErrorMessage("Please answer at least one question before submitting.");
            return;
        }

        setSubmitting(true);
        setMessage("");
        setErrorMessage("");
        setRewardResult(null);

        try {
            const response = await axios.post(`${API_URL}save_survey_response.php`, {
                id,
                answers: payloadAnswers,
            }, {
                withCredentials: true,
            });

            setMessage(response.data?.message || "Thank you for your response! We appreciate your feedback.");
            setRewardResult({
                awardedReward: response.data?.awarded_reward || null,
                rewardNote: response.data?.reward_note || "",
                rewardsRemaining: typeof response.data?.rewards_remaining === "number" ? response.data.rewards_remaining : null,
            });
        } catch (error) {
            console.error("Failed to submit survey", error);
            setErrorMessage(error.response?.data?.message || "Unable to submit your response. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                background: `radial-gradient(circle at top, ${alpha("#6366f1", 0.16)} 0%, transparent 36%), linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)`,
            }}
        >
            <Container maxWidth="md" sx={{ flex: 1, py: { xs: 4, md: 7 }, display: "flex", alignItems: "center" }}>
                <Paper
                    elevation={0}
                    sx={{
                        width: "100%",
                        borderRadius: { xs: 4, md: 5 },
                        p: { xs: 2.5, md: 4.5 },
                        border: "1px solid rgba(99, 102, 241, 0.18)",
                        background: "rgba(255,255,255,0.88)",
                        boxShadow: "0 30px 70px rgba(30, 41, 59, 0.14)",
                    }}
                >
                    {loading ? (
                        <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
                            <CircularProgress />
                        </Box>
                    ) : errorMessage && !survey ? (
                        <Stack spacing={1.5}>
                            <Alert severity="error">{errorMessage}</Alert>
                            {recordMissing && (
                                <Button variant="contained" href={APP_URL} sx={{ textTransform: "none", fontWeight: 700, width: "fit-content" }}>
                                    Go to homepage
                                </Button>
                            )}
                        </Stack>
                    ) : (
                        <Stack spacing={2.25}>
                            <Typography variant="overline" sx={{ color: "#4f46e5", letterSpacing: "0.12em", fontWeight: 800 }}>
                                QR-FLEX SURVEY
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: "#0f172a" }}>
                                {survey?.header || "We value your feedback"}
                            </Typography>
                            <Typography sx={{ color: "#475569", mb: 1.5 }}>
                                {survey?.subheader || "Please answer a few quick questions."}
                            </Typography>

                            {survey?.require_login && !survey?.is_logged_in && (
                                <Alert severity="info">
                                    This survey requires a logged-in QR-Flex account before responses can be submitted.
                                    {" "}
                                    <Link href={buildAppUrl('auth')} underline="hover" sx={{ fontWeight: 700 }}>
                                        Go to login
                                    </Link>
                                </Alert>
                            )}

                            {message && (
                                <Alert severity="success">
                                    <Stack spacing={0.75}>
                                        <Typography sx={{ fontWeight: 700 }}>
                                            {message}
                                        </Typography>
                                        {rewardResult?.awardedReward && (
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                    Your gift reward
                                                </Typography>
                                                <Typography variant="body2" sx={{ mt: 0.2 }}>
                                                    • <strong>{rewardResult.awardedReward.label}:</strong> {rewardResult.awardedReward.value}
                                                </Typography>
                                            </Box>
                                        )}
                                        {rewardResult?.rewardNote && (
                                            <Typography variant="body2">{rewardResult.rewardNote}</Typography>
                                        )}
                                        {rewardResult?.rewardsRemaining !== null && (
                                            <Typography variant="caption" sx={{ color: "#475569" }}>
                                                Remaining gifts: {rewardResult.rewardsRemaining}
                                            </Typography>
                                        )}
                                    </Stack>
                                </Alert>
                            )}
                            {errorMessage && survey && <Alert severity="error">{errorMessage}</Alert>}
                            {completionRewardSummary && !message && (
                                <Alert severity="info">
                                    Gifts are configured for this survey. Distribution is <strong>{completionRewardSummary.distributionMode}</strong>
                                    {completionRewardSummary.giftLimit > 0 ? ` with a limit of ${completionRewardSummary.giftLimit} total gifts.` : " with no total gift limit."}
                                </Alert>
                            )}

                            <Box component="form" onSubmit={handleSubmit}>
                                <Stack spacing={2}>
                                    {(survey?.questions || []).map((question, index) => {
                                        const key = `${question?.label || "question"}-${index}`;
                                        const label = question?.label || `Question ${index + 1}`;
                                        const sharedProps = {
                                            key,
                                            disabled: !canSubmit || submitting,
                                        };

                                        if (question?.type === "star") {
                                            return (
                                                <Box
                                                    key={key}
                                                    sx={{
                                                        p: 2,
                                                        borderRadius: 2.5,
                                                        border: "1px solid rgba(100, 116, 139, 0.24)",
                                                        background: "#fff",
                                                        opacity: !canSubmit || submitting ? 0.7 : 1,
                                                    }}
                                                >
                                                    <Typography variant="subtitle2" sx={{ color: "#64748b", mb: 0.75 }}>
                                                        Question {index + 1}
                                                    </Typography>
                                                    <Typography sx={{ fontWeight: 700, color: "#0f172a", mb: 1.2 }}>
                                                        {label}
                                                    </Typography>
                                                    <Rating
                                                        value={Number(answers[`q_${index}`] || 0)}
                                                        onChange={(_, value) => handleAnswerChange(index, value || 0)}
                                                    />
                                                </Box>
                                            );
                                        }

                                        if (question?.type === "emoji") {
                                            const emojiScale = ["😡", "😕", "😐", "🙂", "😍"];
                                            const selectedValue = Number(answers[`q_${index}`] || 0);
                                            return (
                                                <Box
                                                    key={key}
                                                    sx={{
                                                        p: 2,
                                                        borderRadius: 2.5,
                                                        border: "1px solid rgba(100, 116, 139, 0.24)",
                                                        background: "#fff",
                                                        opacity: !canSubmit || submitting ? 0.7 : 1,
                                                    }}
                                                >
                                                    <Typography variant="subtitle2" sx={{ color: "#64748b", mb: 0.75 }}>
                                                        Question {index + 1}
                                                    </Typography>
                                                    <Typography sx={{ fontWeight: 700, color: "#0f172a", mb: 1.2 }}>
                                                        {label}
                                                    </Typography>
                                                    <Stack direction="row" spacing={1}>
                                                        {emojiScale.map((emoji, emojiIndex) => {
                                                            const value = emojiIndex + 1;
                                                            return (
                                                                <Button
                                                                    key={`${key}-${emoji}`}
                                                                    type="button"
                                                                    variant={selectedValue === value ? "contained" : "outlined"}
                                                                    onClick={() => handleAnswerChange(index, value)}
                                                                    disabled={!canSubmit || submitting}
                                                                    sx={{ minWidth: 48, fontSize: "1.35rem", p: 0.6 }}
                                                                >
                                                                    {emoji}
                                                                </Button>
                                                            );
                                                        })}
                                                    </Stack>
                                                </Box>
                                            );
                                        }

                                        if (question?.type === "thumbs") {
                                            const selectedValue = answers[`q_${index}`] || "";
                                            return (
                                                <Box
                                                    key={key}
                                                    sx={{
                                                        p: 2,
                                                        borderRadius: 2.5,
                                                        border: "1px solid rgba(100, 116, 139, 0.24)",
                                                        background: "#fff",
                                                        opacity: !canSubmit || submitting ? 0.7 : 1,
                                                    }}
                                                >
                                                    <Typography variant="subtitle2" sx={{ color: "#64748b", mb: 0.75 }}>
                                                        Question {index + 1}
                                                    </Typography>
                                                    <Typography sx={{ fontWeight: 700, color: "#0f172a", mb: 1.2 }}>
                                                        {label}
                                                    </Typography>
                                                    <Stack direction="row" spacing={1.2}>
                                                        <Button
                                                            type="button"
                                                            variant={selectedValue === "up" ? "contained" : "outlined"}
                                                            onClick={() => handleAnswerChange(index, "up")}
                                                            disabled={!canSubmit || submitting}
                                                        >
                                                            👍
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            variant={selectedValue === "down" ? "contained" : "outlined"}
                                                            onClick={() => handleAnswerChange(index, "down")}
                                                            disabled={!canSubmit || submitting}
                                                        >
                                                            👎
                                                        </Button>
                                                    </Stack>
                                                </Box>
                                            );
                                        }

                                        if (question?.type === "selection") {
                                            return (
                                                <TextField
                                                    {...sharedProps}
                                                    label={label}
                                                    helperText={`Question ${index + 1}`}
                                                    value={answers[`q_${index}`] || ""}
                                                    onChange={(event) => handleAnswerChange(index, event.target.value)}
                                                    select
                                                >
                                                    {(question?.options || []).map((option) => (
                                                        <MenuItem key={`${key}-${option}`} value={option}>
                                                            {option}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            );
                                        }

                                        return (
                                            <TextField
                                                {...sharedProps}
                                                label={label}
                                                helperText={`Question ${index + 1}`}
                                                value={answers[`q_${index}`] || ""}
                                                onChange={(event) => handleAnswerChange(index, event.target.value)}
                                                multiline
                                                minRows={2}
                                            />
                                        );
                                    })}

                                    {(survey?.questions || []).length === 0 && (
                                        <Alert severity="warning">No survey questions are configured yet.</Alert>
                                    )}

                                    <Button
                                        type="submit"
                                        variant="contained"
                                        size="large"
                                        disabled={!canSubmit || submitting || (survey?.questions || []).length === 0}
                                        sx={{
                                            borderRadius: 999,
                                            textTransform: "none",
                                            fontWeight: 800,
                                            py: 1.25,
                                            background: "linear-gradient(135deg, #4f46e5 0%, #2563eb 100%)",
                                        }}
                                    >
                                        {submitting ? "Submitting..." : "Submit response"}
                                    </Button>
                                </Stack>
                            </Box>
                        </Stack>
                    )}
                </Paper>
            </Container>

            <Box
                component="footer"
                sx={{
                    mt: "auto",
                    py: 2,
                    borderTop: "1px solid rgba(100, 116, 139, 0.18)",
                    backgroundColor: "rgba(255,255,255,0.72)",
                    textAlign: "center",
                }}
            >
                <Typography variant="body2" sx={{ color: "#475569", fontWeight: 700 }}>
                    Powered by{" "}
                    <Link href={APP_URL} target="_blank" rel="noreferrer" sx={{ fontWeight: 800 }}>
                        QR-Flex
                    </Link>
                </Typography>
            </Box>
        </Box>
    );
}
